/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import apiClient from '../../../services/api-client';
import { RootState } from '../../../store/rootReducer';
import hashObject from '../../../util/hash';
import { getProperty } from '../../azure-b2c/storage';
import {
  DeliveryMode,
  deliveryTypeMap,
  GetOrderStatusType,
  OrdersRequestCache,
  OrdersState,
  SalesOrder,
  SalesOrderReducerAction,
  SearchSalesOrdersPayload,
  SearchSalesOrdersResponse,
} from './types';
import { MAX_ORDERS_PAGE_SIZE } from '../utils/consts';
import { URLS } from '../../../util/consts';
import { storageDb } from '../../../services/localforage';

export const ordersPersistConfig = {
  key: 'orders',
  storage: storageDb,
  // serialize: false,
  blacklist: ['salesStatus', 'salesOrder', 'orderStatus'],
};

export const initialState: OrdersState = {
  salesOrders: {},
  salesStatus: {},
  salesOrder: undefined,
  orderStatus: null,
};

const argToHash = (query: SearchSalesOrdersPayload) => {
  return hashObject({
    a: query.startDateTime,
    b: query.endDateTime,
    c: query.salesId,
    d: query.deliveryMode,
    e: query.accountNumber,
  });
};

const transformResponse = (response: SearchSalesOrdersResponse): SalesOrder[] => {
  return response.salesOrders
    .map((e) => transformOrderResponse(e))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

export const transformOrderResponse = (response: SalesOrder): SalesOrder => {
  return {
    ...response,
    deliveryMode: deliveryTypeMap[response.deliveryMode ?? undefined] ?? DeliveryMode.Delivery,
    salesLines: response.salesLines.map((line) => {
      return {
        ...line,
        imageUrl: line.itemId ? URLS.imageBase + 'Products/' + line.itemId + '_000_001.png' : '',
      };
    }),
  };
};

const areSalesOrdersEqual = (order1: SalesOrder, order2: SalesOrder) => {
  return _.isEqual(order1, order2);
};

export const searchOrderHistoryApi = async (
  payload: SearchSalesOrdersPayload,
  signal?: AbortSignal,
): Promise<SalesOrderReducerAction> => {
  const request = await apiClient.post<SearchSalesOrdersResponse, SearchSalesOrdersPayload>(
    `/search-orders`,
    payload,
    {
      signal: signal,
      timeout: 30000,
      timeoutErrorMessage: 'Request timed out. Please try again.',
    },
  );

  return {
    data: transformResponse(request.data),
    accountNumber: payload.accountNumber,
    last: request.data.salesOrders.length < MAX_ORDERS_PAGE_SIZE,
  };
};

let controller = new AbortController();

export const searchOrderHistory = createAsyncThunk<
  SalesOrderReducerAction,
  SearchSalesOrdersPayload
>('orders/search-orders', async (payload) => {
  controller.abort();

  controller = new AbortController();
  const signal = controller.signal;

  let salesId = payload.salesId;

  if (salesId) {
    salesId = `*${salesId}*`;
  } else if (!salesId || salesId === '') {
    salesId = '*';
  }

  return await searchOrderHistoryApi({ ...payload, salesId, skip: payload.skip }, signal);
});

const OrderSlice = createSlice({
  name: 'stores',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(searchOrderHistory.pending, (state: OrdersState, args) => {
      const hash = argToHash(args.meta.arg);
      state.salesStatus[args.meta.arg.accountNumber] = {
        ...state.salesStatus[args.meta.arg.accountNumber],
        [hash]: {
          ...state.salesStatus[args.meta.arg.accountNumber],
          lastRequest: args.meta.arg,
          status: 'pending',
          hash,
        },
      };
    });
    builder.addCase(searchOrderHistory.fulfilled, (state: OrdersState, args) => {
      const hash = argToHash(args.meta.arg);
      const existing = state.salesOrders[args.meta.arg.accountNumber]?.[hash] ?? [];
      const uniqueSalesOrders = _.uniqWith(
        [...existing, ...args.payload.data],
        areSalesOrdersEqual,
      );
      state.salesOrders[args.meta.arg.accountNumber] = {
        ...state.salesOrders[args.meta.arg.accountNumber],
        [hash]: uniqueSalesOrders,
      };
      state.salesStatus[args.meta.arg.accountNumber] = {
        ...state.salesStatus[args.meta.arg.accountNumber],
        [hash]: {
          ...state.salesStatus[args.meta.arg.accountNumber][hash],
          status: 'fulfilled',
          last: args.payload.last,
        },
      };
    });

    builder.addCase(searchOrderHistory.rejected, (state, args) => {
      const hash = argToHash(args.meta.arg);
      state.salesStatus[args.meta.arg.accountNumber] = {
        ...state.salesStatus[args.meta.arg.accountNumber],
        [hash]: { lastRequest: args.meta.arg, status: 'rejected', hash: hash },
      };
    });
  },
  reducers: {
    clear(state: OrdersState) {
      state.salesOrders = {};
      state.salesStatus = {};
    },
  },
});

const selectState = (state: RootState) => state;

export const selectOrderHistory = (id: string) => {
  return createSelector(selectState, (state) => {
    const property = getProperty(state);
    if (state.orders.salesOrders[property as string])
      return state.orders.salesOrders[property as string][id] ?? [];
    return [];
  });
};

export const selectOrderHistoryById = (id: string | undefined) => {
  return createSelector(selectState, (state) => {
    if (id) {
      const property = getProperty(state);

      const dict = new Map<string, SalesOrder>();
      if (property && state.orders.salesOrders[property] !== undefined) {
        const keys = Object.keys(state.orders.salesOrders[property]);
        keys.forEach((key) => {
          state.orders.salesOrders[property][key].forEach((e) => dict.set(e.salesId, e));
        });
      }

      return dict.get(id);
    }

    return undefined;
  });
};

export const selectOrderHistoryStatus = (id: string) => {
  return createSelector(selectState, (state): OrdersRequestCache | null => {
    const property = getProperty(state);

    if (state.orders.salesStatus[property as string])
      return state.orders.salesStatus[property as string][id] ?? null;

    return null;
  });
};

export const selectOrder = createSelector(selectState, (state) => state.orders.salesOrder);

export const selectGetOrderStatus = () => {
  return createSelector(selectState, (state): GetOrderStatusType => {
    return state.orders.orderStatus;
  });
};

export type { OrdersState } from './types';
export default OrderSlice;
